import { useEffect, useRef, useState } from 'react';
import { engine } from '../core/audio/AudioEngine';
import { applyTheme, getStoredTheme, Theme } from '../core/theme';
import { useAppStore } from '../state/store';

function BpmField({ value, onCommit }: { value: number; onCommit: (v: number) => void }) {
  const [text, setText] = useState(value.toFixed(1));
  useEffect(() => {
    setText(value.toFixed(1));
  }, [value]);

  const commit = () => {
    const v = parseFloat(text);
    if (!Number.isNaN(v)) {
      onCommit(Math.min(300, Math.max(30, v)));
    } else {
      setText(value.toFixed(1));
    }
  };

  return (
    <input
      className="readout bpm-input"
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
      }}
      aria-label="Tempo (BPM)"
    />
  );
}

export function TopBar() {
  const isPlaying = useAppStore((s) => s.isPlaying);
  const bpm = useAppStore((s) => s.project.bpm);
  const loopEnabled = useAppStore((s) => s.project.loop.enabled);
  const masterVolume = useAppStore((s) => s.project.masterVolume);
  const timeSig = useAppStore((s) => s.project.timeSig);
  const countIn = useAppStore((s) => s.project.countInBars);
  const recording = useAppStore((s) => s.recording);
  const busy = useAppStore((s) => s.busy);
  const canUndo = useAppStore((s) => s.canUndo);
  const canRedo = useAppStore((s) => s.canRedo);
  const [theme, setTheme] = useState<Theme>(getStoredTheme());
  const [recSource, setRecSource] = useState<'mic' | 'internal'>('internal');
  const posRef = useRef<HTMLSpanElement>(null);
  const loadRef = useRef<HTMLInputElement>(null);
  const importRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    let raf = 0;
    const draw = () => {
      const el = posRef.current;
      if (el) {
        const s = Math.max(0, Math.floor(engine.positionStep));
        el.textContent = `${Math.floor(s / 16) + 1}.${Math.floor((s % 16) / 4) + 1}.${(s % 4) + 1}`;
      }
      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(raf);
  }, []);

  const {
    togglePlay,
    setBpm,
    setLoop,
    setMasterVolume,
    setCountIn,
    setTimeSig,
    toggleRecording,
    bounce,
    saveProject,
    loadProjectFile,
    exportZipFile,
    importZipFile,
    undo,
    redo,
  } = useAppStore.getState();

  return (
    <header className="topbar">
      <div className="brand">
        <span className="brand-mark">≋</span>
        <span className="brand-name">Zangle DAW</span>
      </div>

      <div className="topbar-center">
        <button
          className={`transport-btn ${isPlaying ? 'playing' : ''}`}
          onClick={() => void togglePlay()}
        >
          {isPlaying ? '■ Stop' : '▶ Play'}
        </button>
        <span className="readout" ref={posRef} title="Bar.Beat.Step">
          1.1.1
        </span>
        <span className="io-sep" />
        <button
          className="io-btn btn-undo"
          disabled={!canUndo}
          onClick={undo}
          title="Undo (Ctrl+Z)"
        >
          ↶ Undo
        </button>
        <button
          className="io-btn btn-redo"
          disabled={!canRedo}
          onClick={redo}
          title="Redo (Ctrl+Shift+Z)"
        >
          ↷ Redo
        </button>
        <BpmField value={bpm} onCommit={setBpm} />
        <select
          className="select"
          value={`${timeSig.beats}/${timeSig.unit}`}
          onChange={(e) => {
            const [b, u] = e.target.value.split('/').map(Number);
            setTimeSig(b, u);
          }}
          aria-label="Time signature"
        >
          <option value="4/4">4/4</option>
          <option value="3/4">3/4</option>
          <option value="6/8">6/8</option>
          <option value="7/8">7/8</option>
        </select>
        <select
          className="select"
          value={countIn}
          onChange={(e) => setCountIn(parseInt(e.target.value, 10))}
          title="Count-in before playback"
          aria-label="Count-in"
        >
          <option value={0}>No count-in</option>
          <option value={1}>1 bar</option>
          <option value={2}>2 bars</option>
          <option value={4}>4 bars</option>
        </select>
        <button
          className={`pill-btn ${loopEnabled ? 'active' : ''}`}
          onClick={() => setLoop({ enabled: !loopEnabled })}
          title="Toggle loop"
        >
          ⟳ Loop
        </button>
      </div>

      <div className="topbar-io">
        <select
          className="select rec-src"
          value={recSource}
          onChange={(e) => setRecSource(e.target.value as 'mic' | 'internal')}
          aria-label="Record source"
          disabled={recording.active}
          title="Recording source"
        >
          <option value="internal">Internal</option>
          <option value="mic">Mic</option>
        </select>
        <button
          className={`transport-btn rec btn-rec ${recording.active ? 'recording' : ''}`}
          onClick={() => void toggleRecording(recSource)}
          title="Record the mic or the internal mix; stops and saves to the sample bank"
        >
          {recording.active ? '■ Stop Rec' : '● Rec'}
        </button>
        <button
          className="pill-btn io-btn btn-bounce-wav"
          disabled={!!busy || recording.active}
          onClick={() => void bounce('wav')}
          title="Bounce the current loop to a WAV file"
        >
          Bounce WAV
        </button>
        <button
          className="pill-btn io-btn btn-bounce-mp3"
          disabled={!!busy || recording.active}
          onClick={() => void bounce('mp3')}
          title="Bounce the current loop to an MP3 file"
        >
          Bounce MP3
        </button>
        <button className="pill-btn io-btn btn-save" disabled={!!busy} onClick={() => saveProject()}>
          Save
        </button>
        <button
          className="pill-btn io-btn btn-load"
          disabled={!!busy}
          onClick={() => loadRef.current?.click()}
        >
          Load
        </button>
        <button
          className="pill-btn io-btn btn-export"
          disabled={!!busy}
          onClick={() => void exportZipFile()}
        >
          Export
        </button>
        <button
          className="pill-btn io-btn btn-import"
          disabled={!!busy}
          onClick={() => importRef.current?.click()}
        >
          Import
        </button>
        <input
          ref={loadRef}
          type="file"
          accept=".json,application/json"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void loadProjectFile(f);
            e.target.value = '';
          }}
        />
        <input
          ref={importRef}
          type="file"
          accept=".zip"
          style={{ display: 'none' }}
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) void importZipFile(f);
            e.target.value = '';
          }}
        />
        {busy && <span className="busy-label">{busy}</span>}
      </div>

      <div className="topbar-right">
        <select
          className="select theme-select"
          value={theme}
          onChange={(e) => {
            const t = e.target.value as Theme;
            setTheme(t);
            applyTheme(t);
          }}
          aria-label="Theme"
          title="Color theme"
        >
          <option value="dark">Dark</option>
          <option value="light">Light</option>
        </select>
        <span className="label">Master</span>
        <input
          type="range"
          className="hslider"
          min={0}
          max={1.5}
          step={0.01}
          value={masterVolume}
          onChange={(e) => setMasterVolume(parseFloat(e.target.value))}
          aria-label="Master volume"
        />
      </div>
    </header>
  );
}